import React from "react";
import { Link } from "react-router-dom";
import { footerData } from "../data/mockData";
import { useBookDemo } from "../hooks/useBookDemo";

// Renders a single footer link. Uses <Link> for internal routes and calls
// openBookDemo() for the "Contact Us" action so the demo modal opens.
const FooterLink = ({ link, className, onClick, openBookDemo }) => {
  if (link.action === "book-demo") {
    return (
      <a
        href="#demo"
        onClick={(e) => {
          e.preventDefault();
          openBookDemo();
          if (onClick) onClick();
        }}
        className={className}
        data-testid={`footer-${link.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
      >
        {link.label}
      </a>
    );
  }
  if (link.href && link.href.startsWith("/")) {
    return (
      <Link
        to={link.href}
        onClick={onClick}
        className={className}
        data-testid={`footer-${link.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
      >
        {link.label}
      </Link>
    );
  }
  return (
    <a
      href={link.href}
      onClick={onClick}
      className={className}
      data-testid={`footer-${link.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
    >
      {link.label}
    </a>
  );
};

const Footer = () => {
  const { openBookDemo } = useBookDemo();
  return (
    <footer className="bg-[#121519] text-white relative overflow-hidden">
      {/* DESKTOP / TABLET LANDSCAPE: original layout */}
      <div className="hidden lg:block">
        <div className="max-w-[1300px] mx-auto px-6 md:px-12 lg:px-24">
          {/* Top Row — Logo + Nav */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between pt-16 pb-8">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2 mb-6 md:mb-0">
              <img
                src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/h13tcrhi_image.png"
                alt="Elevante Logo"
                className="h-8 w-auto"
              />
            </a>

            {/* Nav Links */}
            <div className="flex flex-wrap items-center gap-6 md:gap-8">
              {footerData.links.map((link) => (
                <FooterLink
                  key={link.label}
                  link={link}
                  openBookDemo={openBookDemo}
                  className="text-[14px] text-gray-400 hover:text-white transition-colors duration-200"
                />
              ))}
            </div>
          </div>

          {/* Giant Watermark Text */}
          <div className="relative pt-10 md:pt-14 pb-0 overflow-hidden">
            <div className="w-full text-center">
              <div
                className="text-[80px] sm:text-[120px] md:text-[160px] lg:text-[200px] font-bold leading-none tracking-tight select-none"
                style={{ color: "#2a2d35" }}
                aria-hidden="true"
              >
                elevante.ai
              </div>
            </div>
          </div>

          {/* Bottom Row — Copyright + Legal */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between py-6 border-t border-white/10">
            <p className="text-[13px] text-gray-500 mb-4 md:mb-0">
              {footerData.copyright}
            </p>
            <div className="flex items-center gap-6">
              {footerData.legal.map((link) => (
                <FooterLink
                  key={link.label}
                  link={link}
                  openBookDemo={openBookDemo}
                  className="text-[13px] text-gray-500 hover:text-gray-300 transition-colors duration-200"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE / TABLET PORTRAIT: redesigned layout */}
      <div className="lg:hidden relative">
        <div className="px-6 pt-12 pb-6 relative z-10">
          {/* Logo */}
          <a href="/" className="inline-flex items-center mb-10">
            <img
              src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/h13tcrhi_image.png"
              alt="Elevante Logo"
              className="h-8 w-auto"
            />
          </a>

          {/* Vertical nav links */}
          <nav className="flex flex-col gap-6">
            {footerData.links.map((link) => (
              <FooterLink
                key={link.label}
                link={link}
                openBookDemo={openBookDemo}
                className="text-[16px] text-gray-300 hover:text-white transition-colors duration-200"
              />
            ))}
          </nav>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 mx-6" />

        {/* Centered legal block (copyright + Privacy + Terms) on top of watermark */}
        <div className="relative pt-7 pb-12 px-6">
          {/* Watermark — small chevron-only "Elevation" mark, faded, centered behind text */}
          <img
            src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/k7ol4dny_Group%201321317592.png"
            alt=""
            aria-hidden="true"
            className="absolute left-1/2 -translate-x-1/2 top-6 w-[200px] sm:w-[230px] opacity-50 pointer-events-none select-none"
          />
          <div className="relative z-10 flex flex-col items-center gap-5 text-center">
            <p className="text-[13px] text-gray-400">
              {footerData.copyright}
            </p>
            <Link
              to="/privacy"
              className="text-[14px] text-[#7C7E86] hover:text-white transition-colors duration-200"
              data-testid="footer-mobile-privacy-policy"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-[14px] text-[#7C7E86] hover:text-white transition-colors duration-200"
              data-testid="footer-mobile-terms-conditions"
            >
              Terms &amp; Conditions
            </Link>
          </div>
        </div>

        {/* Full-width CTA bar at very bottom — same styling as navbar, dot always visible, arrow slides out on hover */}
        <div className="bg-white px-4 py-3">
          <a
            href="#demo"
            onClick={openBookDemo}
            className="book-demo-btn-footer flex w-full items-stretch rounded-lg border-[1.5px] border-black overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 h-[52px]"
          >
            <span className="book-demo-footer-purple relative z-10 flex flex-1 items-center justify-center gap-2 bg-[#8942FE] text-white text-[15px] font-bold tracking-wide">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#10B981] flex-shrink-0" />
              Book a demo
            </span>
            <span className="book-demo-footer-arrow flex items-center justify-center bg-white border-l-[1.5px] border-black px-5">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
